'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { PublicLink } from '@/components/PublicLink';
import { Download, Loader2, Maximize2, RotateCcw, Trash2, X } from 'lucide-react';

import { AnimatedContainer } from '@/components/ui/animated-container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PreviewDataTable } from '@/components/preview/PreviewDataTable';
import { TemplateRunStatusBadge, TemplateRunTechnicalDetails } from '@/components/templates/TemplateRunStatus';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useSession } from 'next-auth/react';
import { DEFAULT_LAZY_ROWS_PAGE_SIZE, useLazyDatasetRows } from '@/hooks/use-lazy-dataset-rows';
import { useToast } from '@/hooks/use-toast';
import { deleteDataset, exportDatasetCsv, fetchTemplateRunStatus, resumeTemplateRun } from '@/services/templates';
import type { DatasetDetail, DatasetRowsPage, TemplateRunResponse } from '@/types/templates';
import { formatTableCellValue } from '@/lib/tableValue';
import { resolveTemplateRunFailureRowsNote, resolveTemplateRunNoRowsMessage } from '@/lib/templateRunStatus';

type DatasetDetailClientProps = {
  dataset: DatasetDetail;
  initialPreviewPage: DatasetRowsPage | null;
};

const ACTIVE_SOURCE_RUN_STATUSES = new Set(['queued', 'running', 'cancelling']);

export function DatasetDetailClient({
  dataset,
  initialPreviewPage,
}: DatasetDetailClientProps) {
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [sourceRun, setSourceRun] = useState<TemplateRunResponse | null>(null);
  // Run id whose status fetch has finished (successfully or not).
  const [sourceRunSettledId, setSourceRunSettledId] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const router = useRouter();

  const exportAvailable = dataset.exportAvailable !== false && dataset.visibility !== 'public_utility';
  // Owner-only private run datasets; the API enforces the same rule and protects publication datasets.
  // Hidden until the source run status is known; an active resume continuation still gets a 409 from the API.
  const deleteAvailable =
    isAuthenticated &&
    (!dataset.sourceRunId || sourceRunSettledId === dataset.sourceRunId) &&
    dataset.exportAvailable === true &&
    dataset.visibility === 'private' &&
    (!dataset.publication || dataset.publication.publicationKind === 'private_run') &&
    !(sourceRun && ACTIVE_SOURCE_RUN_STATUSES.has(sourceRun.status));
  const schemaColumns = dataset.schema.map((field) => field.key);
  const schemaColumnTypes = useMemo(
    () =>
      dataset.schema.reduce<Record<string, string>>((types, field) => {
        types[field.key] = field.type;
        return types;
      }, {}),
    [dataset.schema],
  );
  const previewRows = useLazyDatasetRows({
    datasetId: dataset.id,
    enabled: true,
    pageSize: DEFAULT_LAZY_ROWS_PAGE_SIZE,
    initialPage: initialPreviewPage,
  });
  const previewColumns = previewRows.columns.length > 0 ? previewRows.columns : schemaColumns;
  const syncPreviewRows = previewRows.sync;
  const sourceRunNoRowsMessage = sourceRun ? resolveTemplateRunNoRowsMessage(sourceRun.status) : null;
  const previewRowsEmpty =
    !previewRows.isLoading &&
    !previewRows.isSyncing &&
    !previewRows.error &&
    previewRows.loadedCount === 0 &&
    !previewRows.hasMore &&
    !previewRows.totalRows;
  const sourceRunFailureRowsNote = resolveTemplateRunFailureRowsNote(
    previewRows.loadedCount > 0 ? previewRows.loadedCount : previewRowsEmpty ? 0 : null,
  );

  useEffect(() => {
    if (!isAuthenticated || !dataset.sourceRunId) {
      setSourceRun(null);
      return;
    }

    let isCurrent = true;
    const loadSourceRun = async () => {
      try {
        const run = await fetchTemplateRunStatus(dataset.sourceRunId as string);
        if (isCurrent) {
          setSourceRun(run);
        }
      } catch {
        if (isCurrent) {
          setSourceRun(null);
        }
      } finally {
        if (isCurrent) {
          setSourceRunSettledId(dataset.sourceRunId as string);
        }
      }
    };
    void loadSourceRun();
    return () => {
      isCurrent = false;
    };
  }, [dataset.sourceRunId, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !sourceRun?.runId || !ACTIVE_SOURCE_RUN_STATUSES.has(sourceRun.status)) {
      return;
    }

    let isCurrent = true;
    const refreshSourceRun = async () => {
      try {
        const run = await fetchTemplateRunStatus(sourceRun.runId);
        if (isCurrent) {
          setSourceRun(run);
          void syncPreviewRows();
        }
      } catch {
        return;
      }
    };
    const interval = window.setInterval(() => {
      void refreshSourceRun();
    }, 2000);
    return () => {
      isCurrent = false;
      window.clearInterval(interval);
    };
  }, [isAuthenticated, sourceRun?.runId, sourceRun?.status, syncPreviewRows]);

  const handleExport = async () => {
    if (!isAuthenticated) {
      toast({
        variant: 'destructive',
        title: 'Требуется вход',
        description: 'Войдите в аккаунт, чтобы экспортировать датасеты.',
      });
      return;
    }
    if (!exportAvailable) {
      toast({
        variant: 'destructive',
        title: 'Экспорт недоступен',
        description: 'Для этого датасета экспорт CSV пока недоступен.',
      });
      return;
    }

    setExporting(true);
    try {
      const { url } = await exportDatasetCsv(dataset.id);
      if (!url) {
        throw new Error('Отсутствует URL для экспорта.');
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Не удалось выполнить экспорт',
        description: err instanceof Error ? err.message : 'Не удалось экспортировать датасет.',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleResume = async () => {
    if (!dataset.sourceTemplateId || !sourceRun?.runId) {
      return;
    }
    setResumeLoading(true);
    try {
      const run = await resumeTemplateRun(dataset.sourceTemplateId, sourceRun.runId);
      setSourceRun(run);
      void syncPreviewRows();
      toast({
        title: 'Сбор продолжен',
        description: 'Продолжаем сбор в этот датасет.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Не удалось продолжить сбор',
        description: err instanceof Error ? err.message : 'Продолжение не удалось.',
      });
    } finally {
      setResumeLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDataset(dataset.id);
      setDeleteDialogOpen(false);
      toast({
        title: 'Датасет удален',
        description: 'Датасет и запуски, которые его собрали, удалены.',
      });
      router.push('/datasets/my');
      router.refresh();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Не удалось удалить датасет',
        description: err instanceof Error ? err.message : 'Удаление не удалось.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AnimatedContainer animation="fadeIn" className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">Превью и схема</h2>
          <p className="mt-2 text-sm leading-6 text-[var(--site-ink-muted)]">
            {dataset.visibility === 'public_utility'
              ? 'Открытый датасет доступен постранично прямо в продукте. Экспорт в V1 отключен.'
              : 'Кэшированные примерные строки доступны прямо в продукте, а экспорт CSV остается способом полного доступа.'}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {sourceRun?.resume?.available && dataset.sourceTemplateId ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleResume()}
              disabled={resumeLoading}
            >
              {resumeLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Продолжение…
                </span>
              ) : (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Продолжить сбор
                </>
              )}
            </Button>
          ) : null}
          {dataset.visibility !== 'public_utility' ? (
            <Button
              onClick={handleExport}
              disabled={!isAuthenticated || exporting || !exportAvailable}
            >
              {exporting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Экспорт…
                </span>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Экспорт CSV
                </>
              )}
            </Button>
          ) : null}
          {dataset.visibility === 'public_preview' ? (
            <PublicLink href="/contact">
              <Button variant="outline">Связаться с продажами</Button>
            </PublicLink>
          ) : null}
          {deleteAvailable ? (
            <Button
              type="button"
              variant="outline"
              className="text-rose-600 hover:text-rose-700"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleting}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Удалить датасет
            </Button>
          ) : null}
        </div>
      </div>

      {sourceRun?.status === 'failed' ? (
        <div
          role="alert"
          className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 [overflow-wrap:anywhere]"
        >
          <div className="flex flex-wrap items-center gap-2">
            <TemplateRunStatusBadge status={sourceRun.status} />
            <span className="font-medium">
              Сбор для этого датасета завершился с ошибкой.{sourceRunFailureRowsNote ? ` ${sourceRunFailureRowsNote}` : ''}
            </span>
          </div>
          <TemplateRunTechnicalDetails messages={[sourceRun.message, sourceRun.errorMessage]} />
          {dataset.sourceTemplateId ? (
            <PublicLink
              href={`/templates/${dataset.sourceTemplateId}`}
              className="mt-2 inline-flex text-[var(--site-accent-text)] hover:underline"
            >
              Вернуться к шаблону →
            </PublicLink>
          ) : null}
        </div>
      ) : sourceRun && ACTIVE_SOURCE_RUN_STATUSES.has(sourceRun.status) ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <TemplateRunStatusBadge status={sourceRun.status} />
          <span>
            {sourceRun.status === 'queued'
              ? 'Сбор в очереди… Строки появятся здесь по мере извлечения.'
              : sourceRun.status === 'cancelling'
                ? 'Сбор останавливается…'
                : 'Сбор выполняется… Строки появятся здесь по мере извлечения.'}
          </span>
        </div>
      ) : (sourceRun?.status === 'completed' || sourceRun?.status === 'completed_partial') && previewRowsEmpty ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-lg border border-[color:var(--site-border-soft)] bg-[var(--site-surface-inset)] px-4 py-3 text-sm text-[var(--site-ink)]"
        >
          <TemplateRunStatusBadge status={sourceRun.status} />
          <span>
            {sourceRun.status === 'completed_partial'
              ? 'Запуск завершен частично, строк не найдено.'
              : 'Запуск завершен, но строк не найдено.'}
          </span>
        </div>
      ) : null}

      <div className="grid min-w-0 gap-6 lg:grid-cols-[2fr_1fr]">
        <Card className="min-w-0">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <CardTitle>Превью</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPreviewDialogOpen(true)}
                disabled={!previewRows.loadedCount && !previewRows.hasMore}
              >
                <Maximize2 className="h-4 w-4" />
                На весь экран
              </Button>
            </div>
            <CardDescription>
              {dataset.visibility === 'public_utility'
                ? 'Опубликованные реальные строки из открытого датасета.'
                : 'Кэшированные примерные строки. Для полного доступа экспортируйте CSV.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {previewRows.isLoading && previewRows.loadedCount === 0 ? (
              <p className="text-sm text-[var(--site-ink-muted)]">Загрузка строк превью…</p>
            ) : previewRows.error && previewRows.loadedCount === 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-rose-600">{previewRows.error}</p>
                <Button type="button" variant="outline" size="sm" onClick={() => void previewRows.retry()}>
                  Повторить
                </Button>
              </div>
            ) : (
              <PreviewDataTable
                tableId={`${dataset.id}-preview`}
                rows={previewRows.rows}
                columns={previewColumns}
                columnTypes={schemaColumnTypes}
                emptyMessage={sourceRunNoRowsMessage ?? 'Строки превью недоступны.'}
                hasMore={previewRows.hasMore}
                isLoadingMore={previewRows.isLoading}
                onLoadMore={previewRows.loadNext}
                loadedCount={previewRows.loadedCount}
                totalCount={previewRows.totalRows}
                loadMoreError={previewRows.error}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Схема</CardTitle>
            <CardDescription>Текущие поля датасета.</CardDescription>
          </CardHeader>
          <CardContent>
            {dataset.schema.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Поле</TableHead>
                    <TableHead>Тип</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataset.schema.map((field) => (
                    <TableRow key={field.key}>
                      <TableCell>{field.key}</TableCell>
                      <TableCell>{formatTableCellValue(field.type)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-[var(--site-ink-muted)]">Схема недоступна.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => (!deleting ? setDeleteDialogOpen(open) : null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить датасет?</DialogTitle>
            <DialogDescription>
              Датасет «{dataset.name}» и запуски, которые его собрали, будут удалены без возможности восстановления.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              <X className="h-4 w-4" />
              Отмена
            </Button>
            <Button type="button" variant="destructive" onClick={() => void handleDelete()} disabled={deleting}>
              {deleting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Удаление…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Удалить датасет
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="flex h-[90vh] max-w-[95vw] flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Превью датасета</DialogTitle>
            <DialogDescription>
              {dataset.visibility === 'public_utility'
                ? 'Опубликованные реальные строки из открытого датасета.'
                : 'Кэшированные примерные строки. Для полного доступа экспортируйте CSV.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded-[1.5rem] border border-[color:var(--site-border-soft)] p-4">
            {previewRows.isLoading && previewRows.loadedCount === 0 ? (
              <p className="text-sm text-[var(--site-ink-muted)]">Загрузка строк превью…</p>
            ) : previewRows.error && previewRows.loadedCount === 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-rose-600">{previewRows.error}</p>
                <Button type="button" variant="outline" size="sm" onClick={() => void previewRows.retry()}>
                  Повторить
                </Button>
              </div>
            ) : (
              <PreviewDataTable
                tableId={`${dataset.id}-preview-dialog`}
                rows={previewRows.rows}
                columns={previewColumns}
                columnTypes={schemaColumnTypes}
                emptyMessage={sourceRunNoRowsMessage ?? 'Строки превью недоступны.'}
                hasMore={previewRows.hasMore}
                isLoadingMore={previewRows.isLoading}
                onLoadMore={previewRows.loadNext}
                loadedCount={previewRows.loadedCount}
                totalCount={previewRows.totalRows}
                loadMoreError={previewRows.error}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AnimatedContainer>
  );
}
